import { getProject } from '@/backend/getProject'
import { removeProperty } from '@/backend/removeProperty'
import { PropertyTool, PropertyType } from '@/backend/types'
import { updateProperty, type UpdatePropertyParams } from '@/backend/updateProperty'
import { updateView } from '@/backend/updateView'
import { useBilling } from '@/modules/Billing/useBilling'
import { useLimitedAction } from '@/modules/Billing/useLimitedAction'
import { useLibraryStore } from '@/modules/Library/libraryStore'
import { getPropertyUpdateConfig } from '@/modules/Project/Properties/getPropertyUpdateConfig'
import type { Property, PropertyInput } from '@/modules/Project/Properties/types'
import { useRelatedProjectsStore } from '@/modules/Project/relatedProjectsStore'
import { serializeProperty } from '@/modules/Project/serializer'
import { useProjectActions } from '@/modules/Project/useProjectActions'
import { useProjectStore } from '@/modules/Project/useProjectStore'
import { usePropertyStore } from '@/modules/Project/usePropertyStore'
import { useShiftPropertyToIndex } from '@/modules/Project/useShiftPropertyIdToIndex'
import { useSubProject } from '@/modules/Project/useSubProject'
import { toast } from '@/shared/toast'
import { invariant } from '@/shared/utils/typeAssertions'
import {
  isCollectionProperty,
  isFileCollectionProperty,
  isSelectProperty,
} from '@/shared/utils/typeGuards'
import { ANALYTICS_EVENT, useAnalytics } from '@/sharedComposables/useAnalytics'
import { ref } from 'vue'

const isSelectParams = (
  params: UpdatePropertyParams,
): params is Extract<
  UpdatePropertyParams,
  { type: 'single_select' | 'multi_select' | 'user_select' | 'hub_select' }
> => ['single_select', 'multi_select', 'user_select', 'hub_select'].includes(params.type)

export const usePropertyDialogIntegration = () => {
  const projectStore = useProjectStore()
  const propertyStore = usePropertyStore()
  const libraryStore = useLibraryStore()

  const subProjectStore = useSubProject()

  const isPropertyBusy = ref(false)

  const deselectProperty = () => propertyStore.stopEditingProperty()

  const cleanupLibraryInputs = (originalProperty: Property, updatedInputs: PropertyInput[]) => {
    const deletedLibraryInputs = originalProperty.inputs.filter((originalInput) => {
      if (originalInput.type !== 'directField') {
        return false
      }
      return !updatedInputs.some((updatedInput) => {
        return (
          updatedInput.type === 'directField' &&
          updatedInput.propertyId === originalInput.propertyId &&
          updatedInput.entityId === originalInput.entityId
        )
      })
    })
    deletedLibraryInputs.forEach((originalInput) => {
      if (originalInput.type !== 'directField') {
        return
      }
      libraryStore.deleteInputReference({
        itemId: originalInput.entityId,
        projectId: originalProperty.projectId,
        propertyId: originalProperty.id,
      })
    })

    updatedInputs
      .filter((updatedInput) => {
        if (updatedInput.type !== 'directField') {
          return false
        }
        return !originalProperty.inputs.some(
          (originalInput) =>
            originalInput.type === 'directField' &&
            originalInput.propertyId === updatedInput.propertyId &&
            originalInput.entityId === updatedInput.entityId,
        )
      })
      .forEach((updatedInput) => {
        if (updatedInput.type !== 'directField') {
          return
        }

        libraryStore.addInputReference({
          itemId: updatedInput.entityId,
          projectId: originalProperty.projectId,
          propertyId: originalProperty.id,
        })
      })
  }

  const reloadAndSetSubProject = async (workspaceId: string, subprojectId: string) => {
    const result = await getProject(workspaceId, subprojectId)
    if (result.ok) {
      invariant(result.data.parent_property, 'Subproject must have a parent property')

      subProjectStore.setProject(subprojectId, {
        id: result.data.id,
        name: result.data.name,
        workspaceId: result.data.workspace_id,
        properties: result.data.properties.map((p) =>
          serializeProperty(p, result.data.workspace_id, result.data.id),
        ),
        parentPropertyId: result.data.parent_property.id,
        updatedAt: result.data.updated_at,
      })
    }
  }

  const { captureAnalyticsEvent } = useAnalytics()

  const relatedProjectsStore = useRelatedProjectsStore()

  const { shiftPropertyToIndex } = useShiftPropertyToIndex()

  const projectActions = useProjectActions()

  /**
   * Store a promise in case we have to
   */
  let createRequest: Promise<Property> | null = null

  type SaveResult =
    | { ok: false; action: 'insert' }
    | { ok: false; error: { code: string; message: string }; action: 'update' }
    | { ok: true; action: 'update' | 'insert'; data: Property }

  const saveEditedProperty = async () => {
    // we have a pending create request, so we wait for it to finish before doing anything else
    // this way, we ensure click spamming in the UI, or dismissing/clicking outside the dialog
    // doesn't dispatch multiple create requests and cause network errors
    if (createRequest) {
      await createRequest
      createRequest = null
    }

    const editedProperty = propertyStore.editedProperty
    invariant(editedProperty)

    const { projectId, workspaceId } = editedProperty

    const originalProperty = propertyStore.originalProperty
    const isInsert = !originalProperty || originalProperty.isOptimistic

    isPropertyBusy.value = true

    // we pass in all these params, as the original implementation,
    // where the function would get all data via useProperty,
    // had race conditions, throwing errors
    const params = getPropertyUpdateConfig(editedProperty, {
      ...propertyStore.getOptionUpdates(),
      upsertCollectionProperties: propertyStore.upsertCollectionProperties,
      removeCollectionProperties: propertyStore.removeCollectionProperties,
    })

    // prepare params

    if (isInsert && isCollectionProperty(editedProperty)) {
      propertyStore.resolveEditedPropertyProperties(editedProperty)
    }

    if (isInsert && isSelectProperty(editedProperty)) {
      invariant(isSelectParams(params), 'Select properties must have options')
      editedProperty.config.options = (params.config.upsertOptions || []).map((o) => ({
        value: o.new_value ?? o.value,
        color: o.color,
        tool_fallback: o.tool_fallback || false,
      }))
    }

    const result = await (async (): Promise<SaveResult> => {
      if (!isInsert) {
        const result = await updateProperty(workspaceId, projectId, originalProperty.id, params)

        return result.ok
          ? {
              ...result,
              action: 'update',
              data: serializeProperty(result.data, workspaceId, projectId),
            }
          : { ...result, action: 'update' }
      }

      // this is an insert

      // an unsaved duplicated property appears right next to the original
      // however, upon save, it will be automatically assigned to the last slot in the table
      // here, we check if this is going to happen, so that later, we can fire off an extra request
      // to move it to the right spot again.
      // this scenario only triggers during property duplication
      const propertyIds = projectStore.activeView?.view.propertyIds || undefined
      const optimisticIndex = propertyIds?.indexOf(editedProperty.id) || -1

      try {
        createRequest = createRequest || projectActions.persistOptimisticProperty(editedProperty)
        const createdProperty = await createRequest
        createRequest = null

        const persistedIndex =
          projectStore.activeView?.view.propertyIds?.indexOf(createdProperty.id) || -1

        if (optimisticIndex !== persistedIndex) {
          await shiftPropertyToIndex(createdProperty.id, optimisticIndex)
        }

        return { ok: true, action: 'insert', data: createdProperty }
      } catch {
        return { ok: false, action: 'insert' }
      }
    })()

    if (!result.ok && result.action === 'update') {
      toast.error(result.error.message)
    }

    if (result.ok && result.action === 'update') {
      invariant(originalProperty)
      cleanupLibraryInputs(originalProperty, editedProperty.inputs)

      projectStore.upsertProperty(result.data)

      const hasUpdatedSubProjectProperties =
        (params.type === 'collection' || params.type === 'file_collection') &&
        (params.config.upsertCollectionProperties?.length ?? 0) +
          (params.config.removeCollectionProperties?.length ?? 0) >
          0
      if (hasUpdatedSubProjectProperties) {
        invariant(
          isFileCollectionProperty(originalProperty) || isCollectionProperty(originalProperty),
        )
        // we need to refetch the subproject properties to update the UI,
        // or stale properties appear in MentionableTextInput
        // see GO-2248 for more
        reloadAndSetSubProject(
          workspaceId,
          originalProperty.config.subprojectConfig.child_project_id,
        )
      }
    }

    if (result.ok && result.action === 'insert' && editedProperty.type === 'reference') {
      await relatedProjectsStore.loadRelatedProject({ ...editedProperty, id: result.data.id })
    }

    if (result.ok) {
      // we now have a persisted id and we were editing an optimistic property
      // so we need to start editing the new property
      propertyStore.startEditingProperty(result.data.id)

      const propertyId = result.data.id
      const { type, tool, description, inputs } = editedProperty

      captureAnalyticsEvent(ANALYTICS_EVENT.PROPERTY_UPDATED, {
        workspaceId,
        projectId,
        propertyId,
      })

      if (tool !== PropertyTool.manual) {
        captureAnalyticsEvent(ANALYTICS_EVENT.AI_PROPERTY_CREATED)
      }

      if (description && inputs.length > 0) {
        captureAnalyticsEvent(ANALYTICS_EVENT.AI_PROMPT_AND_INPUT_SAVED)
      }

      const advancedTypes: Array<typeof type> = [
        PropertyType.url,
        PropertyType.json,
        PropertyType.single_select,
        PropertyType.multi_select,
        PropertyType.collection,
      ]

      if (advancedTypes.includes(type)) {
        captureAnalyticsEvent(ANALYTICS_EVENT.ADVANCED_PROPERTY_SUCCESS, { type })
      }
    }

    isPropertyBusy.value = false

    return result
  }

  const deletePropertyConfirmationOpen = ref(false)

  const billingStore = useBilling()

  const deleteProperty = async () => {
    const property = propertyStore.originalProperty
    if (!property) return

    isPropertyBusy.value = true

    const isOptimistic = propertyStore.editedProperty?.isOptimistic

    const result = isOptimistic
      ? { ok: true } // optimistic properties are not stored in the backend yet
      : await removeProperty(property.workspaceId, property.projectId, property.id)

    if (result.ok) {
      if (billingStore.fieldUsage && !isOptimistic) {
        billingStore.fieldUsage.limitUsage -= projectStore.mainView?.entities?.length ?? 0
      }

      propertyStore.stopEditingProperty()
      projectStore.removeProperty(property.id)
    }

    isPropertyBusy.value = false
    deletePropertyConfirmationOpen.value = false
  }

  const reprocessColumnConfirmationOpen = ref(false)

  const { recalculateEntities } = useLimitedAction()
  const reprocessColumn = async () => {
    const property = propertyStore.originalProperty
    if (!property) return

    isPropertyBusy.value = true

    const result = await recalculateEntities({
      workspaceId: property.workspaceId,
      projectId: property.projectId,
      propertyIds: [property.id],
    })

    isPropertyBusy.value = false

    if (result.ok) {
      if (result.data.affected_count === 0) {
        toast.info('No stale fields found')
      } else {
        toast.info(`Recomputing ${result.data.affected_count} fields`)
      }
    }

    reprocessColumnConfirmationOpen.value = false
  }

  const hideProperty = async () => {
    if (projectStore.activeView) {
      await updateView({
        workspaceId: projectStore.activeView.view.workspaceId,
        projectId: projectStore.activeView.view.projectId,
        viewId: projectStore.activeView.id,
        name: projectStore.activeView.view.name,
        propertyIds:
          projectStore.activeView.view.propertyIds?.filter(
            (id) => id !== propertyStore.originalProperty?.id,
          ) ?? [],
        propertyLayouts: projectStore.activeView.view.propertyLayouts,
        filters: projectStore.activeView.view.filters,
        propertyOptions: projectStore.activeView.view.propertyOptions ?? [],
        assignablePropertyId: projectStore.activeView.view.assignablePropertyId,
      })
      deselectProperty()
    }
  }

  return {
    deleteProperty,
    deletePropertyConfirmationOpen,
    deselectProperty,
    hideProperty,
    isPropertyBusy,
    reprocessColumn,
    reprocessColumnConfirmationOpen,
    saveEditedProperty,
  }
}
